import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';

function ChevronDown({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function NavDropdown({ label, items, location, closeMobile, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const isAnyActive = items.some((it) => location.pathname === it.to);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close when route changes
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="nav-dd" ref={ref}>
      <button
        type="button"
        className={`nav-link nav-dd-trigger ${isAnyActive ? 'nav-link--active' : ''} ${open ? 'nav-dd-trigger--open' : ''}`}
        data-testid={testId}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{label}</span>
        <ChevronDown />
      </button>
      {open && (
        <div className="nav-dd-menu" role="menu" data-testid={`${testId}-menu`}>
          {items.map((it) => {
            const itPath = it.to.split('?')[0];
            const itQuery = it.to.split('?')[1] || '';
            const currentQuery = location.search.replace(/^\?/, '');
            const isActive = location.pathname === itPath && currentQuery === itQuery;
            return (
              <Link
                key={it.to}
                to={it.to}
                role="menuitem"
                className={`nav-dd-item ${isActive ? 'nav-dd-item--active' : ''}`}
                data-testid={it.testId}
                onClick={() => { setOpen(false); closeMobile && closeMobile(); }}
              >
                {it.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NavLinks({ user, location, closeMobile, isMobile }) {
  const lifeMapItems = [
    { to: '/life-map', label: 'Life-Map', testId: 'nav-life-map' },
    { to: '/journey', label: "Your Life's Game", testId: 'nav-journey' },
  ];
  const villageTVItems = [
    { to: '/tv', label: 'Village TV', testId: 'nav-village-tv' },
    { to: '/deepdive', label: 'The DeepDive', testId: 'nav-deepdive' },
  ];
  const dailyItems = [
    { to: '/daily', label: 'The Village Daily', testId: 'nav-daily' },
    { to: '/daily?cat=wednesday-wake-up', label: 'Wednesday Wake-Up', testId: 'nav-wwu' },
  ];

  // Mobile drawer: render flat with indented children (no dropdown UI)
  if (isMobile) {
    return (
      <>
        <Link to="/" className={`nav-link ${location.pathname === '/' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-home">Home</Link>
        <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-about">About</Link>
        <Link to="/book" className={`nav-link ${location.pathname === '/book' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-book">My Book Collection</Link>
        <Link to="/summit" className={`nav-link nav-link--highlight ${location.pathname === '/summit' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-gangsta-ai-seniors">Gangsta AI Seniors</Link>

        <span className="nav-group-label">The Village</span>
        {lifeMapItems.map((it) => (
          <Link key={it.to} to={it.to} className={`nav-link nav-link--sub ${location.pathname === it.to ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid={it.testId}>{it.label}</Link>
        ))}

        {user && (
          <>
            <Link to="/mirror" className={`nav-link ${location.pathname === '/mirror' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-the-mirror">The Mirror</Link>
            <Link to="/journal" className={`nav-link ${location.pathname === '/journal' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-journal">Journal</Link>
          </>
        )}

        <span className="nav-group-label">Watch & Read</span>
        {villageTVItems.map((it) => (
          <Link key={it.to} to={it.to} className={`nav-link nav-link--sub ${location.pathname === it.to ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid={it.testId}>{it.label}</Link>
        ))}
        <Link to="/pull-a-card" className={`nav-link nav-link--highlight ${location.pathname === '/pull-a-card' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-pull-a-card">Pull a Card</Link>
        <Link to="/music" className={`nav-link ${location.pathname === '/music' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-music">Music</Link>
        <Link to="/book-cabrini" className={`nav-link ${location.pathname === '/book-cabrini' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-book-cabrini">Hire Cabrini</Link>

        <span className="nav-group-label">The Village Daily</span>
        {dailyItems.map((it) => (
          <Link key={it.to} to={it.to} className={`nav-link nav-link--sub ${(location.pathname + location.search) === it.to ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid={it.testId}>{it.label}</Link>
        ))}

        {user ? (
          <Link to="/profile" className={`nav-link nav-link--profile ${location.pathname === '/profile' ? 'nav-link--active' : ''}`} onClick={closeMobile} data-testid="nav-profile">
            {user.name?.split(' ')[0]}
          </Link>
        ) : (
          <Link to="/login" className="nav-link nav-link--signin" onClick={closeMobile} data-testid="nav-signin">Sign In</Link>
        )}
      </>
    );
  }

  // Desktop with dropdowns
  return (
    <>
      <Link to="/" className={`nav-link ${location.pathname === '/' ? 'nav-link--active' : ''}`} data-testid="nav-home">Home</Link>
      <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'nav-link--active' : ''}`} data-testid="nav-about">About</Link>
      <Link to="/book" className={`nav-link ${location.pathname === '/book' ? 'nav-link--active' : ''}`} data-testid="nav-book">My Book Collection</Link>
      <Link to="/summit" className={`nav-link nav-link--highlight ${location.pathname === '/summit' ? 'nav-link--active' : ''}`} data-testid="nav-gangsta-ai-seniors">Gangsta AI Seniors</Link>

      <NavDropdown label="Life-Map" items={lifeMapItems} location={location} testId="nav-lifemap-dd" />

      {user && (
        <>
          <Link to="/mirror" className={`nav-link ${location.pathname === '/mirror' ? 'nav-link--active' : ''}`} data-testid="nav-the-mirror">The Mirror</Link>
          <Link to="/journal" className={`nav-link ${location.pathname === '/journal' ? 'nav-link--active' : ''}`} data-testid="nav-journal">Journal</Link>
        </>
      )}

      <NavDropdown label="Village TV" items={villageTVItems} location={location} testId="nav-tv-dd" />

      <Link to="/pull-a-card" className={`nav-link nav-link--highlight ${location.pathname === '/pull-a-card' ? 'nav-link--active' : ''}`} data-testid="nav-pull-a-card">Pull a Card</Link>
      <Link to="/music" className={`nav-link ${location.pathname === '/music' ? 'nav-link--active' : ''}`} data-testid="nav-music">Music</Link>
      <Link to="/book-cabrini" className={`nav-link ${location.pathname === '/book-cabrini' ? 'nav-link--active' : ''}`} data-testid="nav-book-cabrini">Hire Cabrini</Link>

      <NavDropdown label="The Village Daily" items={dailyItems} location={location} testId="nav-daily-dd" />

      {user ? (
        <Link to="/profile" className={`nav-link nav-link--profile ${location.pathname === '/profile' ? 'nav-link--active' : ''}`} data-testid="nav-profile">
          {user.name?.split(' ')[0]}
        </Link>
      ) : (
        <Link to="/login" className="nav-link nav-link--signin" data-testid="nav-signin">Sign In</Link>
      )}
    </>
  );
}

export default function NavBar() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const closeMobile = () => setOpen(false);

  return (
    <>
      <nav className="village-nav" data-testid="village-nav">
        <Link to="/" className="nav-brand">The Alchemy Village</Link>

        <button
          className="nav-toggle"
          data-testid="nav-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <span className={`nav-hamburger ${open ? 'nav-hamburger--open' : ''}`} />
        </button>

        <div className="nav-links nav-links--desktop">
          <NavLinks user={user} location={location} closeMobile={closeMobile} isMobile={false} />
        </div>
      </nav>

      {createPortal(
        <>
          {open && <div className="nav-mobile-backdrop" onClick={closeMobile} data-testid="nav-mobile-backdrop" />}
          <div className={`nav-mobile-drawer ${open ? 'nav-mobile-drawer--open' : ''}`} data-testid="nav-mobile-drawer">
            <NavLinks user={user} location={location} closeMobile={closeMobile} isMobile={true} />
          </div>
        </>,
        document.body
      )}
    </>
  );
}
